import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../lib/api";

const DEFAULT_THEME = {
  accent_color: "#6CABDD",
  ink_color: "#0A0F17",
  heading_scale: "normal",
  body_font: "sans",
  brand_name: "LAST",
  brand_tagline: "Let's All Sing Together · CCFC",
  brand_logo_url: "",
  brand_logo_size: "md",
  splash_header_size: "lg",
  ticker_speed: "normal",
  brand_name_color: "",
  brand_tagline_color: "",
  brand_font: "display",
  favicon_url: "",
  tab_title_suffix: "LAST",
  ticker_bg_color: "#002d75",
  brand_show_name: true,
  brand_show_tagline: true,
  home_tile_1: "/news",
  home_tile_2: "/videos",
  home_tile_3: "/stats",
  footer_title: "LAST — Let's All Sing Together",
  footer_description: "An independent fan-built aggregator for Coventry City supporters. Not affiliated with Coventry City Football Club. All articles and videos link back to their original source.",
  footer_kicker: "Play up Sky Blues",
  footer_tagline: "Built for the Coventry faithful · 2026",
  footer_tagline_url: "",
  page_bg_color: "#FFFFFF",
  header_bg_color: "",
  footer_bg_color: "",
  poll_panel_bg_image: "",
  poll_panel_opacity: 100,
  body_color: "#475569",
  install_button_visible: true,
  home_posts_count: 7,
  brand_logo_pool: [],
  brand_logo_random: false,
  affiliate_disclosure: "As an Amazon Associate, LAST earns from qualifying purchases. Clicking a product opens Amazon in a new tab.",
};

const FONT_FAMILIES = {
  sans: "'Outfit', 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif",
  serif: "'Source Serif Pro', 'Iowan Old Style', 'Georgia', serif",
  mono: "'IBM Plex Mono', 'JetBrains Mono', ui-monospace, monospace",
};

const SCALE_MULTIPLIER = {
  compact: 0.9,
  normal: 1,
  large: 1.12,
};

// Ticker speed → CSS animation duration. Smaller number = faster scroll.
// Tuned so "normal" matches the original hardcoded 45s.
const TICKER_DURATION = {
  slow: "90s",
  normal: "45s",
  fast: "25s",
  very_fast: "12s",
};

const ThemeContext = createContext({ theme: DEFAULT_THEME, refresh: () => {} });

// Cache the last good theme response in localStorage so returning visitors
// see their saved branding + colours on first paint (no flash of defaults).
const THEME_CACHE_KEY = "last:theme:v2";

const readCachedTheme = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(THEME_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (_) {
    return null;
  }
};

const writeCachedTheme = (data) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(THEME_CACHE_KEY, JSON.stringify(data));
  } catch (_) {
    /* non-fatal */
  }
};

const applyToRoot = (theme) => {
  const root = document.documentElement;
  root.style.setProperty("--brand-accent", theme.accent_color || DEFAULT_THEME.accent_color);
  root.style.setProperty("--brand-ink", theme.ink_color || DEFAULT_THEME.ink_color);
  root.style.setProperty(
    "--heading-scale",
    String(SCALE_MULTIPLIER[theme.heading_scale] ?? 1),
  );
  root.style.setProperty("--body-font", FONT_FAMILIES[theme.body_font] || FONT_FAMILIES.sans);
  root.style.setProperty("--ticker-bg", theme.ticker_bg_color || DEFAULT_THEME.ticker_bg_color);
  root.style.setProperty(
    "--ticker-duration",
    TICKER_DURATION[theme.ticker_speed] || TICKER_DURATION.normal,
  );
  root.style.setProperty("--page-bg", theme.page_bg_color || DEFAULT_THEME.page_bg_color);
  root.style.setProperty("--brand-body", theme.body_color || DEFAULT_THEME.body_color);

  // Favicon — find the existing <link rel="icon"> or create one, swap the href.
  const url = theme.favicon_url || DEFAULT_THEME.favicon_url;
  if (url) {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    if (link.href !== url) link.href = url;
  }

  // Apple touch icon — iOS Safari uses THIS, not the manifest, for the
  // home-screen icon when a visitor taps "Add to Home Screen". Without
  // this swap iOS keeps showing the bundled `/last-logo.png` even after
  // the admin uploads a custom PWA icon. Prefer the admin's uploaded
  // "any" PWA icon (already a square 512x512 PNG); fall back to the
  // favicon, then the bundled default.
  const appleIcon =
    theme.pwa_icon_any || theme.favicon_url || DEFAULT_THEME.favicon_url;
  if (appleIcon) {
    let touch = document.querySelector("link[rel='apple-touch-icon']");
    if (!touch) {
      touch = document.createElement("link");
      touch.rel = "apple-touch-icon";
      document.head.appendChild(touch);
    }
    if (touch.getAttribute("href") !== appleIcon) {
      touch.setAttribute("href", appleIcon);
    }
  }
};

export const ThemeProvider = ({ children }) => {
  // Seed from localStorage when available so first paint matches saved branding.
  const [theme, setTheme] = useState(() => {
    const cached = readCachedTheme();
    return cached ? { ...DEFAULT_THEME, ...cached } : DEFAULT_THEME;
  });

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/settings/theme");
      const merged = { ...DEFAULT_THEME, ...r.data };
      // If random-logo mode is on and the admin has uploaded at least one
      // logo to the pool, swap `brand_logo_url` for a random pick so the
      // header + splash render that one for this page load. Keep the
      // chosen URL in `_random_logo_pick` so the admin theme preview can
      // hint which one is showing. We swap *after* merging defaults so
      // localStorage caches the original fixed URL, not the randomised one.
      const pool = Array.isArray(merged.brand_logo_pool)
        ? merged.brand_logo_pool.filter((u) => typeof u === "string" && u.trim())
        : [];
      if (merged.brand_logo_random && pool.length > 0) {
        merged.brand_logo_url = pool[Math.floor(Math.random() * pool.length)];
      }
      setTheme(merged);
      applyToRoot(merged);
      writeCachedTheme(r.data);
    } catch (_) {
      applyToRoot(DEFAULT_THEME);
    }
  }, []);

  useEffect(() => {
    // Apply current state (cached or default) synchronously so first paint
    // is consistent — even before the API responds.
    applyToRoot(theme);
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  const value = useMemo(() => ({ theme, refresh }), [theme, refresh]);

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
